/**
 * Supabase Database Client
 *
 * Centralized Supabase client singleton with secure configuration.
 * All services should import from this module for database access.
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database.types';

// Environment variables - these should be set in .env file
const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').trim();
const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim();

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase URL and/or Anonymous Key not found in environment variables');
}

/**
 * Supabase client with robust authentication and security
 */
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce', // Use PKCE flow for enhanced security
  },
  realtime: {
    params: {
      eventsPerSecond: 10, // Rate limiting for real-time events
    },
  },
  global: {
    headers: {
      'X-Client-Info': 'converlang-web/1.0.0',
      'Content-Type': 'application/json; charset=utf-8', // Explicit UTF-8 encoding for Spanish accents
    },
  },
});

/**
 * Auth debugging utility
 */
export const debugAuthState = async () => {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();
  return { session, error };
};

/**
 * Helper function to clear cached auth data (useful for debugging)
 */
export const clearAuthCache = () => {
  localStorage.removeItem('converlang-auth');
  localStorage.removeItem('supabase.auth.token');
  sessionStorage.clear();
};
